/* ===============================
   SHARED FY + DATE HELPERS
   Put this at: src/utils/fy.js
================================ */

const START_KEYS = ["start_date", "startdate", "from_date", "fromdate", "fy_start", "fystart", "fy_start_date", "start"];
const END_KEYS = ["end_date", "enddate", "to_date", "todate", "fy_end", "fyend", "fy_end_date", "end"];

const pick = (obj, keys) => {
  for (const k of keys) if (obj?.[k]) return obj[k];
  return null;
};

// Plain "YYYY-MM-DD" stays as is; timestamps are converted to the local date.
const dateOnly = (v) =>
  /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? String(v) : toISODate(v);

// Accepts the selectedFinancialYear object from useAppContext ({ fyid, label, ... })
// or a plain label string. Uses the FY's own start/end dates when the object
// has them; otherwise reads the start year from the label
// ("2025-26", "2025-2026", "FY 25-26") and assumes an April-March year.
export const getFYRange = (fy) => {
  if (!fy) return {};

  let label = fy;
  if (typeof fy === "object") {
    const s = pick(fy, START_KEYS);
    const e = pick(fy, END_KEYS);
    if (s && e) return { start: dateOnly(s), end: dateOnly(e) };
    label = fy.label;
  }

  const m = String(label || "").match(/\d{4}|\d{2}/);
  if (!m) return {};
  let startYear = parseInt(m[0], 10);
  if (m[0].length === 2) startYear += 2000;

  return {
    start: `${startYear}-04-01`,
    end: `${startYear + 1}-03-31`,
  };
};

// Local-date based (toISOString shifts the day back in IST)
export const toISODate = (date) => {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// DD/MM/YYYY for display
export const formatDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Picked dates win, but are always clamped inside the selected FY.
export const resolveDateRange = (fy, fromDate, toDate, ignoreDates = false) => {
  const { start, end } = getFYRange(fy);

  let from = (!ignoreDates && toISODate(fromDate)) || start || "";
  let to = (!ignoreDates && toISODate(toDate)) || end || "";

  if (start && from && from < start) from = start;
  if (end && to && to > end) to = end;

  return { from, to };
};

// Print a PDF through a hidden iframe and clean it up afterwards.
export const printPdfDoc = (doc) => {
  const url = URL.createObjectURL(doc.output("blob"));
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 1000);
  };
  iframe.src = url;
};